import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Wallet, Filter, FileText, Plus, Banknote } from 'lucide-react';
import { toast } from 'sonner';
import { downloadBlob } from '../lib/salespro';

export default function Payroll() {
  const { user } = useAuth();
  const isMaster = ['master', 'superadmin'].includes(user?.role_id);
  const [payments, setPayments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ company_id: '', from_date: '', to_date: '' });
  const [payOpen, setPayOpen] = useState(false);
  const [form, setForm] = useState({ user_id: '', concept: 'Salario', period: '', amount: '', method_id: '', method_name: '', reference: '', notes: '' });

  const currentCompanyId = isMaster ? (localStorage.getItem('current_company_id') || user?.company_id) : user?.company_id;

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const init = async () => {
    try {
      const [usersRes, compsRes, methodsRes] = await Promise.all([
        api.getUsers(),
        api.getCompanies(),
        api.getPaymentMethods(),
      ]);
      setEmployees(usersRes.data.filter(u => u.role_id !== 'cliente'));
      setCompanies(compsRes.data);
      setPaymentMethods(methodsRes.data || []);
      await load();
    } catch (e) {
      toast.error('SalesPRO: Error al cargar nómina');
    } finally {
      setLoading(false);
    }
  };

  const load = async () => {
    const params = { tx_type: 'salary_payment' };
    if (filters.company_id) params.company_id = filters.company_id;
    if (filters.from_date) params.from_date = filters.from_date;
    if (filters.to_date) params.to_date = filters.to_date;
    const res = await api.getFinancialTxs(params);
    setPayments(res.data);
  };

  const applyFilters = async () => {
    try { await load(); } catch (e) { toast.error('SalesPRO: Error al filtrar'); }
  };

  const employeeName = (id) => employees.find(u => u.user_id === id)?.full_name || id;
  const companyName = (id) => companies.find(c => c.company_id === id)?.name || id;

  const submitPayment = async () => {
    const amt = parseFloat(form.amount);
    if (!form.user_id) { toast.error('SalesPRO: Selecciona un empleado'); return; }
    if (isNaN(amt) || amt <= 0) { toast.error('SalesPRO: Monto inválido'); return; }
    try {
      const res = await api.payPayroll({
        user_id: form.user_id,
        company_id: currentCompanyId,
        amount: amt,
        concept: form.concept,
        period: form.period || undefined,
        method_id: form.method_id || undefined,
        method_name: form.method_name || undefined,
        reference: form.reference || undefined,
        notes: form.notes || undefined,
      });
      toast.success(res.data.message);
      setPayOpen(false);
      setForm({ user_id: '', concept: 'Salario', period: '', amount: '', method_id: '', method_name: '', reference: '', notes: '' });
      await load();
      // Descargar comprobante
      try {
        const pdf = await api.getPayrollReceipt(res.data.tx_id);
        downloadBlob(new Blob([pdf.data], { type: 'application/pdf' }), `comprobante_${res.data.tx_id.slice(0, 8)}.pdf`);
      } catch (_) { /* ignore */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SalesPRO: Error al registrar pago');
    }
  };

  const downloadReceipt = async (txId) => {
    try {
      const pdf = await api.getPayrollReceipt(txId);
      downloadBlob(new Blob([pdf.data], { type: 'application/pdf' }), `comprobante_${txId.slice(0, 8)}.pdf`);
    } catch (e) {
      toast.error('SalesPRO: Error al descargar comprobante');
    }
  };

  const total = payments.reduce((s, p) => s + Number(p.amount || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-full">Cargando SalesPRO...</div>;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="payroll-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#022C22] flex items-center gap-2">
            <Wallet size={26} /> Nómina / Pagos a Personal
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Registra pagos al personal e impacta el módulo financiero</p>
        </div>
        <Button onClick={() => setPayOpen(true)} data-testid="new-payroll-button">
          <Plus size={16} className="mr-1.5" /> Registrar pago
        </Button>
      </div>

      {/* Filtros */}
      <Card className="border border-[#E2E8F0]">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            {isMaster && (
              <div className="flex-1">
                <Label className="text-xs">Empresa</Label>
                <select
                  value={filters.company_id}
                  onChange={(e) => setFilters({ ...filters, company_id: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white"
                  data-testid="pr-company-filter"
                >
                  <option value="">Todas</option>
                  {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div className="flex-1">
              <Label className="text-xs">Desde</Label>
              <Input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} data-testid="pr-from-date" />
            </div>
            <div className="flex-1">
              <Label className="text-xs">Hasta</Label>
              <Input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} data-testid="pr-to-date" />
            </div>
            <Button onClick={applyFilters} data-testid="pr-apply-filters">
              <Filter size={16} className="mr-1.5" /> Filtrar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Total */}
      <Card className="border border-[#E2E8F0] bg-[#022C22] text-white">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm uppercase tracking-wide text-white/70">
            <Banknote size={16} /> Total pagado ({payments.length} pagos)
          </div>
          <p className="text-2xl font-bold" data-testid="payroll-total">${total.toFixed(2)}</p>
        </CardContent>
      </Card>

      {/* Lista */}
      {payments.length === 0 ? (
        <div className="text-center py-12 text-[#64748B]" data-testid="pr-empty">No hay pagos registrados</div>
      ) : (
        <div className="space-y-2">
          {payments.map((p) => (
            <Card key={p.tx_id} className="border border-[#E2E8F0]" data-testid={`pr-row-${p.tx_id}`}>
              <CardContent className="p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-[#022C22] truncate">{employeeName(p.related_user_id)}</p>
                  <p className="text-xs text-[#64748B] truncate">
                    {p.concept || 'Pago'}{p.period ? ` · ${p.period}` : ''} · {companyName(p.company_id)} · {(p.created_at || '').slice(0, 16).replace('T', ' ')}
                  </p>
                  {p.method_name && <p className="text-xs text-[#94A3B8]">Método: {p.method_name}{p.reference ? ` · Ref: ${p.reference}` : ''}</p>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-bold text-green-700">${Number(p.amount).toFixed(2)}</span>
                  <Button size="sm" variant="outline" onClick={() => downloadReceipt(p.tx_id)} data-testid={`pr-receipt-${p.tx_id}`}>
                    <FileText size={14} className="mr-1" /> Comprobante
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog registrar pago */}
      {payOpen && (
        <Dialog open={payOpen} onOpenChange={setPayOpen}>
          <DialogContent className="max-w-md" data-testid="payroll-dialog">
            <DialogHeader>
              <DialogTitle>Registrar pago a personal</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Empleado *</Label>
                <select
                  value={form.user_id}
                  onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white"
                  data-testid="payroll-employee"
                >
                  <option value="">Selecciona...</option>
                  {employees.map(u => <option key={u.user_id} value={u.user_id}>{u.full_name} ({u.role_id})</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Concepto</Label>
                  <select
                    value={form.concept}
                    onChange={(e) => setForm({ ...form, concept: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white"
                    data-testid="payroll-concept"
                  >
                    <option>Salario</option>
                    <option>Comisión</option>
                    <option>Bono</option>
                    <option>Adelanto</option>
                  </select>
                </div>
                <div>
                  <Label>Periodo</Label>
                  <Input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="Enero 2026" data-testid="payroll-period" />
                </div>
              </div>
              <div>
                <Label>Monto *</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payroll-amount" autoFocus />
              </div>
              <div>
                <Label>Método</Label>
                <select
                  value={form.method_id}
                  onChange={(e) => {
                    const m = paymentMethods.find(pm => pm.method_id === e.target.value);
                    setForm({ ...form, method_id: e.target.value, method_name: m?.name || '' });
                  }}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white"
                  data-testid="payroll-method"
                >
                  <option value="">Sin especificar</option>
                  {paymentMethods.map(m => <option key={m.method_id} value={m.method_id}>{m.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Referencia (opcional)</Label>
                <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="payroll-reference" />
              </div>
              <div>
                <Label>Notas (opcional)</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="payroll-notes" />
              </div>
              <Button onClick={submitPayment} className="w-full" data-testid="submit-payroll">Confirmar pago y generar comprobante</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
